import './MissionDisplay.css';

import React, { useCallback, useState } from 'react';
import { Accordion as AccordionBootstrap, Button, Card, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import { useMutation } from '@apollo/client';

import {
  AccordionProps,
  AchievementMissionsDisplayProps,
  AchievementMissionsProgressResultProps,
  Mission,
} from '../typedefs';
import { RESET_ACHIEVEMENT_PROGRESS } from '../../../graphql';

const AchievementMissionsDisplay: React.FC<AchievementMissionsDisplayProps> = ({
  userId,
  missions,
  missionSetUserId,
}) => {
  const [expanded, setExpanded] = useState(false);

  const [resetForUser, { loading: resetUserLoading }] = useMutation(RESET_ACHIEVEMENT_PROGRESS);

  const onResetThisUser = useCallback(() => {
    if (!missionSetUserId) {
      return;
    }
    if (!window.confirm(`Reset ALL achievement progress for user ${userId}? Test/alpha only.`)) {
      return;
    }
    resetForUser({ variables: { input: { missionSetUserId } } });
  }, [resetForUser, missionSetUserId, userId]);

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <AccordionBootstrap>
          <AccordionBootstrap.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Achievement missions
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </AccordionBootstrap.Toggle>
          <AccordionBootstrap.Collapse eventKey="0">
            <>
              {missions ? (
                <>
                  <div className="mt-2">
                    <Button
                      variant="warning"
                      size="sm"
                      disabled={resetUserLoading || !missionSetUserId}
                      onClick={onResetThisUser}>
                      {resetUserLoading ? <Spinner animation="border" size="sm" /> : 'Reset for this user'}
                    </Button>
                  </div>
                  <div className="mt-2" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    <MissionProgressResults missions={missions} loading={false} />
                  </div>
                </>
              ) : (
                'No missions have been configured for this monthly pass'
              )}
            </>
          </AccordionBootstrap.Collapse>
        </AccordionBootstrap>
      </Card.Body>
    </Card>
  );
};

export default AchievementMissionsDisplay;

const MissionProgressResults: React.FC<AchievementMissionsProgressResultProps> = ({ missions, loading }) => {
  const displayEmptyArray = () => {
    return <div>Empty Array</div>;
  };

  const displayMissions = (data: Mission[]) => {
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Task name</th>
            <th>Reward coins</th>
            <th>Reward diamonds</th>
            <th>Progress</th>
            <th>Claimed date</th>
            <th>Claimed multiplier</th>
          </tr>
        </thead>
        <tbody>
          {data.map((mission: Mission) => (
            <tr key={mission.id}>
              <td>{mission?.mission?.tasks[0]?.type}</td>
              <td>{mission?.mission?.rewardCoins}</td>
              <td>{mission?.mission?.rewardDiamonds}</td>
              <td>
                {mission?.progress}/{mission.progressGoal}
              </td>
              <td>
                {mission?.claimedDate
                  ? moment.unix(mission?.claimedDate / 1000).format('MM/DD/YYYY HH:mm:ss')
                  : 'not available'}
              </td>
              <td>{mission?.claimedMultiplier ? mission?.claimedMultiplier : 'not available'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  if (!missions) {
    return <div>No result</div>;
  }

  return <div>{missions.length > 0 ? displayMissions(missions) : displayEmptyArray()}</div>;
};

const Accordion: React.FC<AccordionProps> = ({ children, active }) => {
  return <div className={`mission_pannel_collapse ${active ? 'active' : ''}`}>{children}</div>;
};
